/**
 * @fileoverview Assignment History Panel Component
 * 
 * Displays assignment history with filtering and audit trail functionality.
 * 
 * Requirements: 4.7
 */

import React, { useState, useEffect, useCallback } from 'react';
import { assignmentService } from '../services/AssignmentService.js';

/**
 * Assignment History Panel Component
 * 
 * @param {Object} props - Component props
 * @param {KnowledgeBaseAssignmentConfig} props.config - Module configuration
 * @param {ModuleContext} props.moduleContext - Module context
 */
export function AssignmentHistoryPanel({ config, moduleContext }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    dateRange: '7d', // 7d, 30d, 90d, all
    operation: 'all', // all, assign, unassign, batch
    status: 'all', // all, success, failed
    userId: 'all'
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0
  });

  // Initialize assignment service
  useEffect(() => {
    assignmentService.initialize(moduleContext.apiClient, config);
  }, [moduleContext.apiClient, config]);

  // Load assignment history
  const loadHistory = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);

    try {
      const queryFilters = {
        page,
        limit: pagination.limit,
        ...filters
      };

      // Convert date range to actual dates
      if (filters.dateRange !== 'all') {
        const days = parseInt(filters.dateRange.replace('d', ''));
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - days);
        queryFilters.startDate = startDate.toISOString();
      }

      const response = await assignmentService.getAssignmentHistory(queryFilters);
      
      setHistory(response.items || []);
      setPagination(prev => ({
        ...prev,
        page,
        total: response.total || 0
      }));

    } catch (err) {
      console.error('Failed to load assignment history:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.limit]);

  // Load history on mount and filter changes
  useEffect(() => {
    loadHistory(1);
  }, [filters]);

  // Handle filter change
  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  // Handle page change
  const handlePageChange = useCallback((newPage) => {
    loadHistory(newPage);
  }, [loadHistory]);

  // Format date for display
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  // Get operation icon
  const getOperationIcon = (operation) => {
    switch (operation) {
      case 'assign': return '➕';
      case 'unassign': return '➖';
      case 'batch_assign': return '📦';
      case 'batch_unassign': return '📤';
      default: return '📝';
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return 'green';
      case 'failed': return 'red';
      case 'partial': return 'yellow';
      default: return 'gray';
    }
  };

  if (loading && history.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">Loading history...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
        <div className="flex items-center space-x-3">
          <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <h3 className="text-lg font-semibold text-red-800 dark:text-red-200">Failed to Load History</h3>
            <p className="text-red-600 dark:text-red-400">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 dark:bg-gray-900">
        <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Filters</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Date Range */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 dark:text-gray-600">
              Date Range
            </label>
            <select
              value={filters.dateRange}
              onChange={(e) => handleFilterChange('dateRange', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white dark:bg-gray-800"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="all">All time</option>
            </select>
          </div>

          {/* Operation */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 dark:text-gray-600">
              Operation
            </label>
            <select
              value={filters.operation}
              onChange={(e) => handleFilterChange('operation', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white dark:bg-gray-800"
            >
              <option value="all">All operations</option>
              <option value="assign">Assign</option>
              <option value="unassign">Unassign</option>
              <option value="batch">Batch operations</option>
            </select>
          </div>

          {/* Status */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 dark:text-gray-600">
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white dark:bg-gray-800"
            >
              <option value="all">All statuses</option>
              <option value="success">Success</option>
              <option value="failed">Failed</option>
              <option value="partial">Partial</option>
            </select>
          </div>

          {/* Refresh */}
          <div className="flex items-end">
            <button
              onClick={() => loadHistory(1)}
              disabled={loading}
              className="w-full px-3 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:opacity-50 dark:text-white"
            >
              {loading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>

      {/* History List */}
      <div className="space-y-3">
        {history.length === 0 ? (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No history found</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              No assignment history matches your current filters.
            </p>
          </div>
        ) : (
          history.map((entry) => (
            <HistoryEntry key={entry.id} entry={entry} />
          ))
        )}
      </div>

      {/* Pagination */}
      {pagination.total > pagination.limit && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700 dark:text-gray-300 dark:text-gray-600">
            Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} entries
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page <= 1}
              className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 dark:bg-gray-800 dark:bg-gray-900 dark:text-gray-600"
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page >= Math.ceil(pagination.total / pagination.limit)}
              className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 dark:bg-gray-800 dark:bg-gray-900 dark:text-gray-600"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Individual History Entry Component
 */
function HistoryEntry({ entry }) {
  const [expanded, setExpanded] = useState(false);

  const getOperationIcon = (operation) => {
    switch (operation) {
      case 'assign': return '➕';
      case 'unassign': return '➖';
      case 'batch_assign': return '📦';
      case 'batch_unassign': return '📤';
      default: return '📝';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return 'green';
      case 'failed': return 'red';
      case 'partial': return 'yellow';
      default: return 'gray';
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <span className="text-lg">{getOperationIcon(entry.operation)}</span>
          </div>
          <div className="flex-1">
            <div className="flex items-center space-x-2">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                {entry.operation.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </h3>
              <span className={`px-2 py-1 text-xs rounded-full bg-${getStatusColor(entry.status)}-100 dark:bg-${getStatusColor(entry.status)}-900/30 text-${getStatusColor(entry.status)}-700 dark:text-${getStatusColor(entry.status)}-300`}>
                {entry.status}
              </span>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 dark:text-gray-300 dark:text-gray-600">
              {entry.description || `${entry.operation} operation`}
            </p>
            <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500 dark:text-gray-500">
              <span>By: {entry.userName || entry.userId}</span>
              <span>At: {new Date(entry.createdAt).toLocaleString()}</span>
              {entry.affectedCount && (
                <span>Affected: {entry.affectedCount} items</span>
              )}
            </div>
          </div>
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 dark:text-gray-300 dark:text-gray-600"
        >
          <svg className={`h-5 w-5 transform transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            {entry.details && (
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">Details</h4>
                <pre className="text-xs text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-900 p-2 rounded overflow-x-auto dark:text-gray-300 dark:text-gray-600">
                  {JSON.stringify(entry.details, null, 2)}
                </pre>
              </div>
            )}
            {entry.error && (
              <div>
                <h4 className="font-medium text-red-800 dark:text-red-200 mb-2">Error</h4>
                <p className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-2 rounded">
                  {entry.error}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AssignmentHistoryPanel;
